import type { LoaderFunctionArgs } from '~/types/route-args';
import type { ShouldRevalidateFunction } from 'react-router';
import { data as routerData } from 'react-router';
import type { Logger } from '@archipro-website/logger/server';
import { querySearchServiceSearch } from '@modules/search';
import { getProductsFromESSearchResults } from '@modules-rocco/search/data-mappers/tile-product';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { DEFAULT_SOURCE_FILTER } from '@modules/search/util/filter-url-helpers';
import { RedisLRUCache } from '@modules-rocco/cache/redis-lru-cache.server';
import {
    getCuratedCollectionProductsCached,
    getBuildProductsCached,
} from '~/modules/curated-collection/api/get-curated-collection-products-cached.server';

export const EDITORS_CHOICE_PRODUCTS_ACTION =
    '/actions/editors-choice-products';

/** Max concurrent OpenSearch queries to avoid connection pool pressure. */
const MAX_CONCURRENT_QUERIES = 5;

let editorsChoiceCache: RedisLRUCache<ProductTileViewModel[]> | null = null;

function getCache(logger: Logger): RedisLRUCache<ProductTileViewModel[]> {
    if (!editorsChoiceCache) {
        editorsChoiceCache = new RedisLRUCache<ProductTileViewModel[]>({
            maxSize: 200,
            ttlMs: 30 * 60 * 1000, // 30 min in-memory
            redisTtlMs: 24 * 60 * 60 * 1000, // 24 hour Redis
            namespace: 'editors-choice-products',
            logger,
        });
    }
    return editorsChoiceCache;
}

/**
 * Fetch products for a single category link via the search service.
 * Returns empty array on error or null search results.
 */
async function fetchProductsForCategory(
    categoryLink: string,
    context: Parameters<typeof querySearchServiceSearch>[0]['context'],
): Promise<ProductTileViewModel[]> {
    const searchResults = await querySearchServiceSearch({
        context,
        queryParams: {
            searchType: 'product',
            visibilityType: 'directory',
            filters: { categoryLink },
            size: 12,
            sortBy: 'featured',
            sourceFilter: DEFAULT_SOURCE_FILTER,
        },
    });

    return searchResults
        ? getProductsFromESSearchResults(searchResults, null)
        : [];
}

/**
 * API endpoint to fetch products for the EditorsChoice section.
 *
 * Routing:
 * - Multiple categoryLink params: Batch fetch for all categories (category pages pre-load)
 * - Single categoryLink param: Search service with category filter (category pages)
 * - source=build: Products from latest Build newsletter (root page, Building toggle)
 * - Default: Products from latest Curated Collection newsletter (root page, Interior toggle)
 *
 * Fallback strategy: Query search service for featured products (sortBy: 'featured').
 * Returns up to 12 products per category as ProductTileViewModel[].
 */
export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);
    const categoryLinks = url.searchParams.getAll('categoryLink');
    const source = url.searchParams.get('source');

    // Batch mode: fetch products for one or more categories in parallel
    // Always returns mode: 'batch' so the client can build a productsByCategory map
    if (categoryLinks.length >= 1) {
        const cache = getCache(logger);
        const productsByCategory: Record<string, ProductTileViewModel[]> = {};

        for (let i = 0; i < categoryLinks.length; i += MAX_CONCURRENT_QUERIES) {
            const batch = categoryLinks.slice(i, i + MAX_CONCURRENT_QUERIES);
            const settled = await Promise.allSettled(
                batch.map((link) =>
                    cache.getOrFetch(link, () =>
                        fetchProductsForCategory(link, context),
                    ),
                ),
            );

            settled.forEach((result, j) => {
                const link = batch[j];
                if (!link) return;
                if (result.status === 'rejected') {
                    logger.error('Batch category fetch failed', {
                        categoryLink: link,
                        error: result.reason,
                    });
                }
                productsByCategory[link] =
                    result.status === 'fulfilled' ? (result.value ?? []) : [];
            });
        }

        return { mode: 'batch' as const, productsByCategory };
    }

    // Root /products page: Get products from newsletter based on source
    const fetchProducts =
        source === 'build'
            ? getBuildProductsCached
            : getCuratedCollectionProductsCached;

    try {
        const products = await fetchProducts(context);
        return {
            mode: 'single' as const,
            products: products.slice(0, 12),
        };
    } catch (error) {
        logger.warning(
            `${source === 'build' ? 'Build' : 'Curated collection'} products failed, falling back to search service`,
            { error },
        );
    }

    // Fallback for root page: Generic featured products
    try {
        const searchResults = await querySearchServiceSearch({
            context,
            queryParams: {
                searchType: 'product',
                visibilityType: 'directory',
                filters: {},
                size: 12,
                sortBy: 'featured',
                sourceFilter: DEFAULT_SOURCE_FILTER,
            },
        });

        if (!searchResults) {
            logger.error('Fallback search service returned non-OK response');
            return routerData(
                { mode: 'single' as const, products: [] },
                { status: 500 },
            );
        }

        const products = getProductsFromESSearchResults(searchResults, null);
        return { mode: 'single' as const, products };
    } catch (error) {
        logger.error('Fallback search service also failed', { error });
        return routerData(
            { mode: 'single' as const, products: [] },
            { status: 500 },
        );
    }
};

export const shouldRevalidate: ShouldRevalidateFunction = () => {
    return false;
};
